import {
  computed,
  inject,
  onBeforeUnmount,
  onMounted,
  onUpdated,
  provide,
  reactive,
  type InjectionKey,
  type Ref,
} from 'vue';

// Rows of a list report the width of their first part and all get the widest as minimum width.
type AlignedTargetWidth = {
  widths: Map<symbol, number>;
  width: Readonly<Ref<number>>;
};

const alignedTargetWidthKey: InjectionKey<AlignedTargetWidth> = Symbol('alignedTargetWidth');

export function provideAlignedTargetWidth() {
  const widths = reactive(new Map<symbol, number>());
  const width = computed(() => Math.max(0, ...widths.values()));
  provide(alignedTargetWidthKey, { widths, width });
}

export function useAlignedTargetWidth(
  measured: Ref<HTMLElement | null>,
  enabled: () => boolean,
): Readonly<Ref<number>> {
  const context = inject(alignedTargetWidthKey, null);
  const id = Symbol('row');

  function measure() {
    if (!context) return;
    if (!enabled() || !measured.value) {
      context.widths.delete(id);
      return;
    }
    context.widths.set(id, Math.ceil(measured.value.getBoundingClientRect().width));
  }

  onMounted(measure);
  onUpdated(measure);
  onBeforeUnmount(() => context?.widths.delete(id));

  return computed(() => (context && enabled() ? context.width.value : 0));
}
